import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Activity,
    Search,
    Filter,
    Calendar,
    User,
    Server,
    Shield,
    AlertCircle,
    Clock,
    Download,
    Eye,
    FileText,
    RefreshCw,
    Trash2,
    MoreVertical,
    ChevronDown,
    ChevronUp
} from "lucide-react";
import BackButton from "../../components/BackButton";

const ViewLogs = () => {
    const [logs, setLogs] = useState([
        {
            id: 1,
            action: "User Login",
            user: "admin@teamhub.com",
            ip: "192.168.1.100",
            status: "success",
            timestamp: "2024-11-27 09:00:00",
            details: "Successful login from Chrome on Windows",
            severity: "low"
        },
        {
            id: 2,
            action: "Role Created",
            user: "admin@teamhub.com",
            ip: "192.168.1.100",
            status: "success",
            timestamp: "2024-11-27 09:15:00",
            details: "Created new role 'Project Manager' with 12 permissions",
            severity: "medium"
        },
        {
            id: 3,
            action: "Permission Modified",
            user: "admin@teamhub.com",
            ip: "192.168.1.100",
            status: "success",
            timestamp: "2024-11-27 09:30:00",
            details: "Updated permission 'Edit Projects' for role 'Team Member'",
            severity: "medium"
        },
        {
            id: 4,
            action: "Failed Login Attempt",
            user: "unknown@example.com",
            ip: "203.0.113.25",
            status: "failed",
            timestamp: "2024-11-27 10:00:00",
            details: "Invalid password attempt from Firefox on MacOS",
            severity: "high"
        },
        {
            id: 5,
            action: "System Backup",
            user: "system",
            ip: "127.0.0.1",
            status: "success",
            timestamp: "2024-11-27 11:00:00",
            details: "Automated daily backup completed successfully",
            severity: "low"
        },
        {
            id: 6,
            action: "User Deleted",
            user: "admin@teamhub.com",
            ip: "192.168.1.100",
            status: "success",
            timestamp: "2024-11-27 12:00:00",
            details: "Deleted user 'inactive@teamhub.com' from the system",
            severity: "high"
        },
        {
            id: 7,
            action: "Database Query",
            user: "system",
            ip: "127.0.0.1",
            status: "success",
            timestamp: "2024-11-27 13:00:00",
            details: "Optimized database indexes for better performance",
            severity: "low"
        },
        {
            id: 8,
            action: "Security Alert",
            user: "security@teamhub.com",
            ip: "192.168.1.150",
            status: "warning",
            timestamp: "2024-11-27 14:00:00",
            details: "Multiple failed login attempts detected from suspicious IP",
            severity: "critical"
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSeverity, setSelectedSeverity] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [expandedLog, setExpandedLog] = useState(null);
    const [dateRange, setDateRange] = useState("today");

    const severityLevels = [
        { value: "all", label: "All Severities", color: "gray" },
        { value: "low", label: "Low", color: "green" },
        { value: "medium", label: "Medium", color: "blue" },
        { value: "high", label: "High", color: "orange" },
        { value: "critical", label: "Critical", color: "red" }
    ];

    const statusOptions = [
        { value: "all", label: "All Statuses" },
        { value: "success", label: "Success" },
        { value: "failed", label: "Failed" },
        { value: "warning", label: "Warning" }
    ];

    const dateRanges = [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "week", label: "Last 7 days" },
        { value: "month", label: "Last 30 days" },
        { value: "all", label: "All time" }
    ];

    const getSeverityColor = (severity) => {
        switch (severity) {
            case "critical": return "bg-red-100 text-red-800 border-red-200";
            case "high": return "bg-orange-100 text-orange-800 border-orange-200";
            case "medium": return "bg-blue-100 text-blue-800 border-blue-200";
            case "low": return "bg-green-100 text-green-800 border-green-200";
            default: return "bg-gray-100 text-gray-800 border-gray-200";
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "success": return "bg-green-100 text-green-800";
            case "failed": return "bg-red-100 text-red-800";
            case "warning": return "bg-yellow-100 text-yellow-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getActionIcon = (action) => {
        if (action.includes("Login")) return <User className="w-4 h-4" />;
        if (action.includes("Role")) return <Shield className="w-4 h-4" />;
        if (action.includes("Permission")) return <FileText className="w-4 h-4" />;
        if (action.includes("Security")) return <AlertCircle className="w-4 h-4" />;
        return <Activity className="w-4 h-4" />;
    };

    const filteredLogs = logs.filter(log => {
        const matchesSearch =
            log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.details.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesSeverity = selectedSeverity === "all" || log.severity === selectedSeverity;
        const matchesStatus = selectedStatus === "all" || log.status === selectedStatus;

        return matchesSearch && matchesSeverity && matchesStatus;
    });

    const clearAllLogs = () => {
        if (window.confirm("Are you sure you want to clear all logs? This action cannot be undone.")) {
            setLogs([]);
        }
    };

    const exportLogs = () => {
        const dataStr = JSON.stringify(logs, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `activity-logs-${new Date().toISOString().split('T')[0]}.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const refreshLogs = () => {
        // In a real app, this would fetch from API
        // For now, we'll just show a loading state
        setTimeout(() => {
            // Simulate refresh
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/admin/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Activity Logs</h1>
                                <p className="text-gray-600 mt-1">Monitor and review system activities</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={refreshLogs}
                                className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={exportLogs}
                                className="flex items-center px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export Logs
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Filters and Stats */}
                <div className="mb-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search logs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <select
                                value={selectedSeverity}
                                onChange={(e) => setSelectedSeverity(e.target.value)}
                                className="input-field"
                            >
                                {severityLevels.map(level => (
                                    <option key={level.value} value={level.value}>
                                        {level.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field"
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range */}
                        <div className="flex items-center space-x-3">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <div className="flex flex-wrap gap-2">
                                {dateRanges.map(range => (
                                    <button
                                        key={range.value}
                                        onClick={() => setDateRange(range.value)}
                                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                            dateRange === range.value
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Logs</p>
                                    <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
                                </div>
                                <Activity className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Security Alerts</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {logs.filter(l => l.severity === 'critical' || l.severity === 'high').length}
                                    </p>
                                </div>
                                <AlertCircle className="w-8 h-8 text-red-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Success Rate</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {Math.round((logs.filter(l => l.status === 'success').length / logs.length) * 100)}%
                                    </p>
                                </div>
                                <Shield className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Unique Users</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {new Set(logs.map(l => l.user)).size}
                                    </p>
                                </div>
                                <User className="w-8 h-8 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logs Table */}
                <div className="card overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">Activity Logs</h2>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={clearAllLogs}
                                    className="flex items-center px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Severity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timestamp
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {filteredLogs.map((log) => (
                                <React.Fragment key={log.id}>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <div className="text-gray-600">
                                                        {getActionIcon(log.action)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{log.action}</div>
                                                    <div className="text-sm text-gray-500">{log.ip}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{log.user}</div>
                                        </td>
                                        <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(log.severity)}`}>
                          {log.severity.charAt(0).toUpperCase() + log.severity.slice(1)}
                        </span>
                                        </td>
                                        <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <Clock className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm text-gray-600">{log.timestamp}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                    title={expandedLog === log.id ? "Collapse" : "Expand"}
                                                >
                                                    {expandedLog === log.id ? (
                                                        <ChevronUp className="w-4 h-4" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4" />
                                                    )}
                                                </button>
                                                <button
                                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedLog === log.id && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="space-y-4"
                                                >
                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                                                            <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                                                                {log.details}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Info</h4>
                                                            <div className="space-y-2">
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">Log ID:</span>
                                                                    <span className="font-medium text-gray-900">LOG-{log.id.toString().padStart(6, '0')}</span>
                                                                </div>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">IP Address:</span>
                                                                    <span className="font-medium text-gray-900">{log.ip}</span>
                                                                </div>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">Source:</span>
                                                                    <span className="font-medium text-gray-900">Web Application</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                                        <button className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                                            Mark as Reviewed
                                                        </button>
                                                        <button className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                                            Create Alert
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredLogs.length === 0 && (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No logs found</h3>
                            <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                {searchTerm || selectedSeverity !== 'all' || selectedStatus !== 'all'
                                    ? "No logs match your search criteria. Try adjusting your filters."
                                    : "No activity logs have been recorded yet. Activity will appear here as it occurs."
                                }
                            </p>
                            {(searchTerm || selectedSeverity !== 'all' || selectedStatus !== 'all') && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedSeverity('all');
                                        setSelectedStatus('all');
                                    }}
                                    className="btn-primary"
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {filteredLogs.length > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    Showing <span className="font-medium">1</span> to{' '}
                                    <span className="font-medium">{filteredLogs.length}</span> of{' '}
                                    <span className="font-medium">{logs.length}</span> logs
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                        Previous
                                    </button>
                                    <button className="px-3 py-1.5 border border-gray-300 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                        1
                                    </button>
                                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                        2
                                    </button>
                                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Recent Activity Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                >
                    <div className="card">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Activity Timeline</h2>
                        <div className="space-y-4">
                            {logs.slice(0, 5).map((log, index) => (
                                <div key={log.id} className="flex items-start">
                                    <div className="flex-shrink-0 relative">
                                        <div className={`w-3 h-3 rounded-full mt-1 ${
                                            log.severity === 'critical' ? 'bg-red-500' :
                                                log.severity === 'high' ? 'bg-orange-500' :
                                                    log.severity === 'medium' ? 'bg-blue-500' : 'bg-green-500'
                                        }`} />
                                        {index < 4 && (
                                            <div className="absolute top-3 left-1.5 w-0.5 h-8 bg-gray-200" />
                                        )}
                                    </div>
                                    <div className="ml-4 flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-gray-900">{log.action}</span>
                                            <span className="text-sm text-gray-500">{log.timestamp.split(' ')[1]}</span>
                                        </div>
                                        <p className="text-sm text-gray-600">{log.user}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ViewLogs;